import { CoreStore, handleStoreState } from "../RESTStore/CoreStore";
import { observable, action, runInAction, makeObservable } from "mobx";
import { API } from "General/API";
import { ArrayDataStore } from "./ArrayDataStore";
import { MapDataStore } from "./MapDataStore";
import merge from "deepmerge";
import { deepMerge } from "utils/deepMerge";

export abstract class SubmitStore/**
 * T - Request object interface
 * R - Response object interface. TODO: is it possible to set initial value R = T ?
 * D - DataStore type either Array or Map
 */ <
  T extends object,
  R extends T,
  D extends ArrayDataStore<R> | MapDataStore<string | number, R> = null
> extends CoreStore {
  data: T;
  _dataStore: D;

  constructor(dataStore?: D) {
    super();

    makeObservable(this, {
      data: observable,
      clear: action,
      setData: action.bound,
      updateData: action.bound,
      post: action,
      put: action,
      delete: action,
      fetch: action,
    });

    this._dataStore = dataStore;
    this.data = {} as T;
  }

  clear() {
    this.data = {} as T;
    this.state = "RAW";
    this.stateError = undefined;
  }

  setData(data: T) {
    this.data = data;
    this.state = "DONE";
  }

  updateData(newData: { [P in keyof T]?: T[P] }) {
    this.data = merge(JSON.parse(JSON.stringify(this.data)), JSON.parse(JSON.stringify(newData)), {
      arrayMerge: (destinationArray, sourceArray) => sourceArray,
    });
    // this.data = { ...this.data as Object, ...newData as Object } as T;
  }

  @handleStoreState()
  async post(formData: T) {
    try {
      const submitData = deepMerge(this.data, formData);
      const response: R = await API.newData(this.url(), submitData);
      runInAction(() => {
        if (this._dataStore) {
          this._dataStore.add(response);
        }
        this.data = deepMerge(this.data, response) as T;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async put(id: number | string, formData: T) {
    try {
      const submitData = deepMerge(this.data, formData) as R;
      await API.editData(this.url(id), submitData);
      runInAction(() => {
        if (this._dataStore) {
          this._dataStore.edit(id, submitData);
        }
        this.data = submitData as T;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async delete(id: string | number) {
    try {
      await API.deleteData(this.url(id));
      runInAction(() => {
        if (this._dataStore) {
          this._dataStore.delete(id);
        }
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetch(id: string | number) {
    try {
      const response: R = await API.getData(this.url(id));
      runInAction(() => {
        this.data = response;
      });
      return response;
    } catch (err) {
      throw err;
    }
  }
}
